import { Component, ReactNode } from 'react';
import { AlertTriangle, RotateCcw, Home, Bug } from 'lucide-react';
import { clearGameSession } from '../api/client';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: string;
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: '' };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: '' };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const info = `${error.toString()}\n${errorInfo.componentStack}`;
    this.setState({ errorInfo: info });

    // Log to console and localStorage for debugging
    console.error('ErrorBoundary caught:', error, errorInfo);
    const logs = JSON.parse(localStorage.getItem('planet_error_logs') || '[]');
    logs.push({
      time: new Date().toISOString(),
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    });
    // Keep last 20 errors
    if (logs.length > 20) logs.shift();
    localStorage.setItem('planet_error_logs', JSON.stringify(logs));
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    clearGameSession();
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      const isDev = import.meta.env.DEV;
      return (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-950">
          <div className="w-full max-w-md mx-4 p-6 rounded-2xl bg-slate-900 border border-red-800/40 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-xl bg-red-900/40">
                <AlertTriangle className="w-6 h-6 text-red-400" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Something went wrong</h2>
                <p className="text-xs text-slate-400">Your planet encountered an unexpected problem.</p>
              </div>
            </div>

            <div className="bg-slate-800/60 rounded-lg p-3 mb-4">
              <p className="text-sm text-red-300 font-mono break-words">
                {this.state.error?.message || 'Unknown error'}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-2 mb-4">
              <button
                onClick={this.handleReload}
                className="flex items-center justify-center gap-2 px-4 py-2.5 bg-cyan-600 hover:bg-cyan-500 rounded-xl text-sm font-semibold text-white transition-colors"
              >
                <RotateCcw className="w-4 h-4" />
                Try Again
              </button>
              <button
                onClick={this.handleGoHome}
                className="flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-700 hover:bg-slate-600 rounded-xl text-sm font-semibold text-white transition-colors"
              >
                <Home className="w-4 h-4" />
                Go Home
              </button>
            </div>

            {isDev && this.state.errorInfo && (
              <details className="text-xs">
                <summary className="flex items-center gap-1 text-slate-500 cursor-pointer hover:text-slate-400">
                  <Bug className="w-3 h-3" />
                  Debug info
                </summary>
                <pre className="mt-2 p-2 bg-slate-950 rounded-lg text-slate-400 overflow-auto max-h-48 whitespace-pre-wrap">
                  {this.state.errorInfo}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
